import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useSafeIsFocused } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import { useUserLocation } from '@order/shared/hooks';

import { checkIfLateOrder, checkIfPastOrder } from '../../utils';
import { useChallengeCelebration } from '../useChallengeCelebration';
import { useMomoSurveyUrl } from '../useMomoSurveyUrl';
import { useOrderHealthKit } from '../useOrderHealthKit';
import { useOrderStatusPolling } from '../useOrderStatusPolling';
import { useOrderStatusRequest, type UseOrderStatusRequestParams } from '../useOrderStatusRequest';

export function useOrderStatusData(params: UseOrderStatusDataParams) {
  const { isLoggedIn, customerId, customerFirstName, customerLastName, customerEmail, onReorder } =
    params;

  const { formatMessage } = useIntl();
  const isRenderedOnScreen = useSafeIsFocused();

  // ─── Request ─────────────────────────────────────────────────────────

  const requestParams: UseOrderStatusRequestParams = {
    isLoggedIn,
    customerId,
  };

  const response = useOrderStatusRequest(requestParams);

  const {
    order,
    courierDetails,
    orderStatus,
    orderId,
    orderType,
    isInStoreOrder,
    isFetching: isFetchingOrderStatus,
    refetchCanCancelStatus,
  } = response;

  // ─── Order Status Polling ────────────────────────────────────────────

  const {
    status,
    courier,
    refetchOrderStatus: refetchOrderStatusData,
  } = useOrderStatusPolling(orderId, orderType, orderStatus, courierDetails);

  const refetchOrderStatus = useCallback(() => {
    refetchCanCancelStatus();
    refetchOrderStatusData();
  }, [refetchCanCancelStatus, refetchOrderStatusData]);

  // ─── Derived Statuses ────────────────────────────────────────────────

  const isLate = checkIfLateOrder({ order, flattenedOrderStatus: status });
  const isPast = checkIfPastOrder({ order, flattenedOrderStatus: status });

  // ─── Health Kit ──────────────────────────────────────────────────────

  const { isOrderToHealthKitEnabled, orderToHealthKitLineItems } = useOrderHealthKit(order);

  // ─── User Data ───────────────────────────────────────────────────────

  const customerName = formatMessage(messages.you);
  const userLocationData = useUserLocation({
    pollingInterval: GEO_POLLING_INTERVAL,
  });
  const { geolocation } = userLocationData;
  const { latitude: userLat, longitude: userLon } = geolocation ?? {};

  // ─── Reorder ─────────────────────────────────────────────────────────

  const handleReorder = onReorder(order);

  // ─── Momo Survey ─────────────────────────────────────────────────────

  const momoSurveyUrl = useMomoSurveyUrl({
    isInStoreOrder,
    orderId,
    orderTime: order?.wantedTime ?? '',
    restaurantEntityId: String(order?.restaurant?.entityId ?? ''),
    customerId: isLoggedIn ? customerId : undefined,
    firstName: isLoggedIn ? customerFirstName : undefined,
    lastName: isLoggedIn ? customerLastName : undefined,
    email: isLoggedIn ? customerEmail : undefined,
  });

  // ─── Challenge Celebration ───────────────────────────────────────────

  const challengeData = useChallengeCelebration(orderId);
  const { celebration, fetching: isFetchingCelebration } = challengeData;

  // ─── Data ────────────────────────────────────────────────────────────

  return {
    ...response,
    isFetching: isFetchingOrderStatus || isFetchingCelebration,

    isRenderedOnScreen,

    status,

    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,

    isLate,
    isPast,

    customerName,
    customerLatitude: userLat ?? 0,
    customerLongitude: userLon ?? 0,

    courierName: courier?.name,
    courierPhone: courier?.phone,
    courierLatitude: courier?.latitude,
    courierLongitude: courier?.longitude,

    momoSurveyUrl,
    celebration,

    onReorder: handleReorder,
    refetchOrderStatus,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  you: {
    defaultMessage: 'You',
    description: 'Order Status | Status Data Fetch | Customer Name',
  },
});

// ─── Constants ─────────────────────────────────────────────────────────

const GEO_POLLING_INTERVAL = 60 * 1000; // 60 seconds.

// ─── Types ───────────────────────────────────────────────────────────────────

export type UseOrderStatusDataParams = {
  isLoggedIn: boolean;
  customerId: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  onReorder: (order: Partial<Order> | undefined) => () => void;
};
